import * as z from "zod";
import { PmsType, WifiSecurity } from "@/types/hotel";

const GOOGLE_PLACE_ID_PATTERN = /^[A-Za-z0-9_-]{10,200}$/;
const GOOGLE_MAPS_CID_PATTERN = /^\d{5,25}$/;

const optionalPatternField = (pattern: RegExp, message: string) =>
  z
    .string()
    .trim()
    .refine((value) => value === "" || pattern.test(value), message)
    .optional();

export const hotelSchema = z.object({
  name: z
    .string()
    .trim()
    .nonempty()
    .regex(/^[\p{L}\p{N}\s.,'-]+$/u, "Invalid Input"),
  address: z.object({
    street: z
      .string()
      .trim()
      .nonempty("Street is required")
      .regex(
        /^[\p{L}\p{N}\s,.\-'#/]+$/u,
        "Street must contain valid characters",
      ),
    city: z
      .string()
      .trim()
      .nonempty("City is required")
      .regex(/^[\p{L}\s.\-']+$/u, "City must contain valid characters"),
    state: z
      .string()
      .trim()
      .regex(/^[\p{L}\s.\-']*$/u, "State must contain valid characters"),
    country: z
      .string()
      .trim()
      .nonempty("Country is required")
      .length(2, "Country must be a 2-letter ISO code")
      .regex(/^[A-Z]{2}$/, "Country must be a 2-letter ISO code"),
    postalCode: z
      .string()
      .trim()
      .regex(/^[\p{L}\p{N}\s]*$/u, "Postal code must contain valid characters"),
    googlePlaceId: optionalPatternField(
      GOOGLE_PLACE_ID_PATTERN,
      "Place ID must be 10-200 letters, digits, _ or -",
    ),
    googleMapsCid: optionalPatternField(
      GOOGLE_MAPS_CID_PATTERN,
      "CID must be 5-25 digits",
    ),
  }),
  wifiNetwork: z.string().nonempty(),
  // Empty for an open network (WifiSecurity.NONE).
  wifiPassword: z.string(),
  wifiSecurity: z.enum(WifiSecurity),
  managerIds: z.array(z.string()).min(1, "At least one manager is required"),
  pms: z.enum(PmsType),

  logoId: z.string().optional(),
  mapFileId: z.string().optional(),
  policyText: z.string().optional(),
  timezone: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  checkInTime: z.string().nullable().optional(),
  checkOutTime: z.string().nullable().optional(),
  socialMediaInstagram: z.string().nullable().optional(),
  socialMediaFacebook: z.string().nullable().optional(),
  socialMediaOther: z.string().nullable().optional(),

  chatSettings: z
    .object({
      isAiEnabled: z.boolean(),
      isTranslationEnabled: z.boolean(),
      defaultLanguage: z.string().nonempty(),
    })
    .optional(),
  paymentSettings: z
    .object({
      paymentEnabled: z.boolean(),
      currency: z.string().optional(),
    })
    .optional(),
});

export const hotelUpdateSchema = hotelSchema.extend({
  managerIds: z.array(z.string()),
});

export type HotelFormData = z.infer<typeof hotelSchema>;
